import { DateFormat } from '@agroone/dates'
import { IsDateValidate, IsValidSiret } from '@agroone/helpers'
import { plainToInstance, Type } from 'class-transformer'
import {
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsInt,
  IsISO8601,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  Matches,
  ValidateIf,
  ValidateNested,
} from 'class-validator'
import { INSEE_CODE_REGEX } from '../constants'
import { FarmType } from '../enums/farm-type.enum'
import { Regions } from '../enums/regions.enum'
import { Contact } from './contact'
import { FarmCooperativesDto } from './farm-cooperatives'
import { OldFarm } from './old-farm'

export class Farm {
  active: boolean | undefined
  address?: string
  agreoCode?: string
  businessNumber?: string
  campaignId?: number | null
  certificationEndDate?: string | null
  certificationOrganicNumber?: string
  certificationStartDate?: string | null
  contacts: Contact[] | undefined
  cooperatives?: FarmCooperativesDto[]
  farmCsrId?: number
  farmName: string | undefined
  id: number | undefined
  /** INSEE commune code of the main address, only set where `CountryRules.hasInseeCode` holds. */
  inseeCode?: string
  legalStructureId?: number
  metadata?: string
  name: string | undefined
  oldFarms: OldFarm[] | undefined
  organization?: string
  regions: { id: number; name: string }[] | undefined
  shippingAddress?: string
  shippingInseeCode?: string
  shippingTown?: string
  shippingZipcode?: string
  technician?: string
  town?: string
  zipcode?: string
  type?: { id: number; key: FarmType }

  constructor(farm?: Farm) {
    this.id = farm?.id
    this.active = farm?.active
    this.name = farm?.name
    this.farmName = farm?.farmName
    this.regions = farm?.regions
    this.oldFarms = farm?.oldFarms
    this.contacts = farm?.contacts
    this.agreoCode = farm?.agreoCode
    this.organization = farm?.organization
    this.technician = farm?.technician
    this.certificationOrganicNumber = farm?.certificationOrganicNumber
    this.certificationStartDate = farm?.certificationStartDate
    this.certificationEndDate = farm?.certificationEndDate
    this.metadata = farm?.metadata
    this.cooperatives = farm?.cooperatives
    this.farmCsrId = farm?.farmCsrId
    this.address = farm?.address
    this.shippingAddress = farm?.shippingAddress
    this.zipcode = farm?.zipcode
    this.shippingZipcode = farm?.shippingZipcode
    this.shippingTown = farm?.shippingTown
    this.town = farm?.town
    this.inseeCode = farm?.inseeCode
    this.shippingInseeCode = farm?.shippingInseeCode
    this.legalStructureId = farm?.legalStructureId
    this.businessNumber = farm?.businessNumber
    this.type = farm?.type
  }
}

export class SaveFarmContact {
  @IsNumber()
  @IsOptional()
  id: number

  @IsString()
  @IsNotEmpty()
  firstName: string

  @IsString()
  @IsNotEmpty()
  lastName: string

  @IsNumber()
  @IsOptional()
  functionId?: number | null

  @IsNumber()
  @IsNotEmpty()
  languageId: number

  @IsArray()
  @IsOptional()
  phoneNumbers: string[]

  @IsArray()
  @IsOptional()
  phoneNumbersSMS: string[]

  @IsString()
  @IsOptional()
  emailAddress: string

  @IsNumber()
  @IsNotEmpty()
  priority: number

  @IsBoolean()
  @IsNotEmpty()
  active: boolean

  constructor(
    id: number,
    firstName: string,
    lastName: string,
    languageId: number,
    phoneNumbers: string[],
    phoneNumbersSMS: string[],
    emailAddress: string,
    priority: number,
    active: boolean,
    functionId?: number,
  ) {
    this.id = id
    this.firstName = firstName
    this.lastName = lastName
    this.languageId = languageId
    this.phoneNumbers = phoneNumbers
    this.phoneNumbersSMS = phoneNumbersSMS
    this.emailAddress = emailAddress
    this.priority = priority
    this.active = active
    this.functionId = functionId
  }
}

export class SaveFarmCooperative {
  @IsNumber()
  @IsPositive()
  @IsInt()
  @IsNotEmpty()
  id!: number

  @IsArray()
  @IsNumber({}, { each: true })
  @IsPositive({ each: true })
  @IsInt({ each: true })
  cropTypes!: number[]
}

export class SaveFarm {
  public static build(raw: object): SaveFarm {
    return plainToInstance(SaveFarm, raw)
  }

  @IsNumber()
  @IsOptional()
  id?: number

  @IsString()
  @IsNotEmpty()
  farmName: string | undefined

  @IsNumber()
  @IsOptional()
  legalStructureId?: number | undefined

  @IsNumber()
  typeId?: number | undefined

  @IsArray()
  @IsString({ each: true })
  @IsNotEmpty()
  @ArrayNotEmpty()
  regions: string[] | undefined

  @IsBoolean()
  @IsNotEmpty()
  active: boolean | undefined

  @ArrayNotEmpty()
  @ValidateNested({ each: true })
  @Type(() => SaveFarmContact)
  contacts: SaveFarmContact[] | undefined

  @IsString()
  @IsOptional()
  agreoCode?: string

  @IsString()
  @IsOptional()
  organization?: string

  @IsString()
  @IsOptional()
  technician?: string

  @IsString()
  @IsOptional()
  certificationOrganicNumber?: string

  @IsOptional()
  @IsDateValidate(DateFormat.YYYY_MM_DD)
  certificationStartDate?: string | null

  @IsOptional()
  @IsDateValidate(DateFormat.YYYY_MM_DD)
  certificationEndDate?: string | null

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SaveFarmCooperative)
  cooperatives?: SaveFarmCooperative[]

  @IsOptional()
  @IsString()
  address: string | undefined

  @IsOptional()
  @IsString()
  zipcode: string | undefined

  @IsOptional()
  @IsString()
  town: string | undefined

  @IsOptional()
  @IsString()
  shippingAddress?: string

  @IsOptional()
  @IsString()
  shippingZipcode?: string

  @IsOptional()
  @IsString()
  shippingTown?: string

  @IsOptional()
  @IsString()
  @Matches(INSEE_CODE_REGEX)
  inseeCode?: string

  @IsOptional()
  @IsString()
  @Matches(INSEE_CODE_REGEX)
  shippingInseeCode?: string

  @IsOptional()
  @IsISO8601()
  fieldsChangedDate?: string

  @IsString()
  @IsOptional()
  @ValidateIf(
    (o) =>
      o.businessNumber &&
      o.regions &&
      o.regions.some((region: Regions) =>
        [Regions.FRANCE_NORTH, Regions.FRANCE_SOUTH_WEST, Regions.FRANCE].includes(region),
      ),
  )
  @IsValidSiret()
  businessNumber?: string

  constructor(farm?: SaveFarm) {
    this.id = farm?.id
    this.cooperatives = farm?.cooperatives
    this.farmName = farm?.farmName
    this.regions = farm?.regions
    this.active = farm?.active
    this.contacts = farm?.contacts
    this.agreoCode = farm?.agreoCode
    this.organization = farm?.organization
    this.technician = farm?.technician
    this.certificationOrganicNumber = farm?.certificationOrganicNumber
    this.certificationStartDate = farm?.certificationStartDate
    this.certificationEndDate = farm?.certificationEndDate
    this.address = farm?.address
    this.shippingAddress = farm?.shippingAddress
    this.zipcode = farm?.zipcode
    this.shippingZipcode = farm?.shippingZipcode
    this.town = farm?.town
    this.shippingTown = farm?.shippingTown
    this.inseeCode = farm?.inseeCode
    this.shippingInseeCode = farm?.shippingInseeCode
    this.legalStructureId = farm?.legalStructureId
    this.fieldsChangedDate = farm?.fieldsChangedDate
    this.businessNumber = farm?.businessNumber
    this.typeId = farm?.typeId
  }
}

export interface FarmRequest {
  farmId: number
  farmName: string
  farmActive: number
  agreoCode: string
  organization: string
  technician: string
  certificationOrganicNumber: string
  certificationStartDate: string
  certificationEndDate: string
  regionId: number
  regionName: string
  contactId: number
  contactFirstName: string
  contactLastName: string
  contactLanguageId: number
  contactPhoneNumbers: string
  contactPhoneNumbersSMS: string
  contactEmailAddress: string
  contactPriority: number
  contactActive: boolean
  contactLanguage: string
  contactFunctionId?: number
  cooperativeName: string
  cooperativeId: number
  farmCsrId: number
  campaignId?: number
  address?: string
  shippingAddress?: string
  zipcode?: string
  shippingZipcode?: string
  town?: string
  shippingTown?: string
  inseeCode?: string
  shippingInseeCode?: string
  legalStructureId?: number
  businessNumber?: string
  // old‑farm info returned when joining old-farms table
  oldFarmId?: number
  oldFarmName?: string
  cooperativeCropTypeId?: number
  cooperativeCropTypeKey?: string
  cropTypeFarmId?: number
}

export class FarmDto {
  id?: number
  name: string | undefined
  active: number
  agreoCode?: string
  organization?: string
  technician?: string
  certificationOrganicNumber?: string
  certificationStartDate?: string | null
  certificationEndDate?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: number
  updatedBy?: number
  cooperative?: FarmCooperativesDto
  address?: string
  shippingAddress?: string
  zipcode?: string
  shippingZipcode?: string
  town?: string
  shippingTown?: string
  inseeCode?: string
  shippingInseeCode?: string
  legalStructureId?: number
  businessNumber?: string

  constructor(farm?: SaveFarm) {
    this.id = farm?.id
    this.name = farm?.farmName
    this.legalStructureId = farm?.legalStructureId
    this.active = farm?.active ? 1 : 0
    this.agreoCode = farm?.agreoCode
    this.organization = farm?.organization
    this.technician = farm?.technician
    this.certificationOrganicNumber = farm?.certificationOrganicNumber
    this.certificationStartDate = farm?.certificationStartDate
    this.certificationEndDate = farm?.certificationEndDate

    // addresses
    this.address = farm?.address
    this.shippingAddress = farm?.shippingAddress
    this.zipcode = farm?.zipcode
    this.town = farm?.town
    this.shippingTown = farm?.shippingTown
    this.shippingZipcode = farm?.shippingZipcode
    this.inseeCode = farm?.inseeCode
    this.shippingInseeCode = farm?.shippingInseeCode
    this.businessNumber = farm?.businessNumber
  }
}

export class FarmContact {
  firstName!: string
  lastName!: string
  mailAddress?: string
  phoneNumbers!: string[]
  language!: string
  priority!: number
  active!: boolean

  public static build(raw: any) {
    return plainToInstance(FarmContact, raw)
  }
}
